import { useEffect, useState } from 'react';
import axios from 'axios';
import { BookOpen, Plus, Edit, Trash2, Loader2, X, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BRANCHES = ['CS', 'EC', 'EE', 'Instrumentation'];

const emptyQuestion = {
  question: '',
  options: ['', '', '', ''],
  correct_answer: 0,
  explanation: ''
};

export default function AdminQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuiz, setEditingQuiz] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedQuiz, setExpandedQuiz] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    branch: 'CS',
    subject: '',
    questions: [{ ...emptyQuestion }]
  });

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/admin/quizzes`, getAuthHeaders());
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      toast.error('Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (quizId) => {
    if (!window.confirm('Are you sure you want to delete this quiz? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/quizzes/${quizId}`, getAuthHeaders());
      toast.success('Quiz deleted successfully');
      fetchQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Failed to delete quiz');
    }
  };

  const openCreateModal = () => {
    setEditingQuiz(null);
    setFormData({
      title: '',
      branch: 'CS',
      subject: '',
      questions: [{ ...emptyQuestion }]
    });
    setShowModal(true);
  };

  const openEditModal = (quiz) => {
    setEditingQuiz(quiz);
    setFormData({
      title: quiz.title,
      branch: quiz.branch,
      subject: quiz.subject,
      questions: quiz.questions.map(q => ({
        question: q.question,
        options: [...q.options],
        correct_answer: q.correct_answer,
        explanation: q.explanation || ''
      }))
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Please enter a quiz title');
      return;
    }
    if (!formData.subject.trim()) {
      toast.error('Please enter a subject');
      return;
    }
    if (formData.questions.length === 0) {
      toast.error('Please add at least one question');
      return;
    }

    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (!q.question.trim()) {
        toast.error(`Question ${i + 1} is empty`);
        return;
      }
      const emptyOptions = q.options.filter(o => !o.trim()).length;
      if (emptyOptions > 0) {
        toast.error(`Question ${i + 1} has empty options`);
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        title: formData.title,
        branch: formData.branch,
        subject: formData.subject,
        questions: formData.questions.map(q => ({
          question: q.question,
          options: q.options,
          correct_answer: q.correct_answer,
          explanation: q.explanation || ''
        }))
      };

      if (editingQuiz) {
        await axios.put(`${API}/admin/quizzes/${editingQuiz.quiz_id}`, payload, getAuthHeaders());
        toast.success('Quiz updated successfully');
      } else {
        await axios.post(`${API}/admin/quizzes`, payload, getAuthHeaders());
        toast.success('Quiz created successfully');
      }

      setShowModal(false);
      fetchQuizzes();
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error('Failed to save quiz');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    setFormData(prev => ({
      ...prev,
      questions: [...prev.questions, { ...emptyQuestion, options: ['', '', '', ''] }]
    }));
  };

  const removeQuestion = (index) => {
    if (formData.questions.length === 1) {
      toast.error('Quiz must have at least one question');
      return;
    }
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const updateOption = (qIndex, oIndex, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === qIndex 
          ? { ...q, options: q.options.map((o, j) => j === oIndex ? value : o) }
          : q
      )
    }));
  };

  const groupByBranch = () => {
    const grouped = {};
    quizzes.forEach((quiz) => {
      if (!grouped[quiz.branch]) {
        grouped[quiz.branch] = [];
      }
      grouped[quiz.branch].push(quiz);
    });
    return grouped;
  };

  const groupedQuizzes = groupByBranch();

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-dark font-display">Manage Quizzes</h1>
          <p className="text-slate-600 mt-2">Total Quizzes: {quizzes.length}</p>
        </div>
        <Button
          data-testid="create-quiz-btn"
          onClick={openCreateModal}
          className="btn-primary"
        >
          <Plus className="h-5 w-5 mr-2" />
          Create Quiz
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="inline-block h-8 w-8 animate-spin text-brand-blue" />
          <p className="mt-4 text-slate-600">Loading quizzes...</p>
        </div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">No quizzes created yet.</p>
          <Button onClick={openCreateModal} className="btn-primary">
            <Plus className="h-5 w-5 mr-2" />
            Create Your First Quiz
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.keys(groupedQuizzes).sort().map((branch) => (
            <div key={branch}>
              <h2 className="text-2xl font-bold text-brand-dark mb-4 pb-2 border-b-2 border-brand-blue">
                {branch}
              </h2>
              <div className="space-y-4">
                {groupedQuizzes[branch].map((quiz) => (
                  <div
                    key={quiz.quiz_id}
                    data-testid={`quiz-card-${quiz.quiz_id}`}
                    className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="p-4 bg-slate-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <BookOpen className="h-6 w-6 text-brand-blue" />
                            <div>
                              <h3 className="text-lg font-semibold text-brand-dark">{quiz.title}</h3>
                              <p className="text-sm text-brand-blue">{quiz.subject}</p>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-slate-600">
                            <span className="bg-brand-blue/10 px-2 py-1 rounded text-brand-blue font-medium">
                              {quiz.questions.length} Questions
                            </span>
                            <span className="ml-3 text-xs text-slate-500">
                              Created: {new Date(quiz.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            data-testid={`expand-quiz-${quiz.quiz_id}`}
                            onClick={() => setExpandedQuiz(expandedQuiz === quiz.quiz_id ? null : quiz.quiz_id)}
                            variant="outline"
                            size="sm"
                          >
                            {expandedQuiz === quiz.quiz_id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            data-testid={`edit-quiz-${quiz.quiz_id}`}
                            onClick={() => openEditModal(quiz)}
                            variant="outline"
                            size="sm"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            data-testid={`delete-quiz-${quiz.quiz_id}`}
                            onClick={() => handleDelete(quiz.quiz_id)}
                            variant="destructive"
                            size="sm"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    {expandedQuiz === quiz.quiz_id && (
                      <div className="p-4 border-t border-slate-200 bg-white">
                        <h4 className="font-semibold text-brand-dark mb-3">Questions Preview</h4>
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                          {quiz.questions.map((q, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium text-brand-dark mb-2">
                                {idx + 1}. {q.question}
                              </p>
                              <div className="grid grid-cols-2 gap-2 text-sm">
                                {q.options.map((opt, oIdx) => (
                                  <div
                                    key={oIdx}
                                    className={`p-2 rounded ${
                                      oIdx === q.correct_answer
                                        ? 'bg-green-100 text-green-800 border border-green-300'
                                        : 'bg-white border border-slate-200'
                                    }`}
                                  >
                                    {String.fromCharCode(65 + oIdx)}. {opt}
                                  </div>
                                ))}
                              </div>
                              {q.explanation && (
                                <p className="mt-2 text-sm text-slate-600 italic">
                                  Explanation: {q.explanation}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-brand-dark">
                {editingQuiz ? 'Edit Quiz' : 'Create New Quiz'}
              </h2>
              <Button
                data-testid="close-modal-btn"
                onClick={() => setShowModal(false)}
                variant="ghost"
                size="sm"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="title">Quiz Title *</Label>
                    <Input
                      id="title"
                      data-testid="quiz-title-input"
                      value={formData.title}
                      onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Data Structures Basics"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="branch">Branch *</Label>
                    <select
                      id="branch"
                      data-testid="quiz-branch-select"
                      value={formData.branch}
                      onChange={(e) => setFormData(prev => ({ ...prev, branch: e.target.value }))}
                      className="mt-1 w-full h-10 px-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue"
                    >
                      {BRANCHES.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      data-testid="quiz-subject-input"
                      value={formData.subject}
                      onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="e.g., Programming"
                      className="mt-1"
                    />
                  </div>
                </div>

                {/* Questions */}
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <Label className="text-lg">Questions ({formData.questions.length})</Label>
                    <Button
                      type="button"
                      data-testid="add-question-btn"
                      onClick={addQuestion}
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Question
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {formData.questions.map((q, qIdx) => (
                      <div
                        key={qIdx}
                        data-testid={`question-block-${qIdx}`}
                        className="p-4 border border-slate-200 rounded-lg bg-slate-50"
                      >
                        <div className="flex justify-between items-start mb-3">
                          <Label className="text-brand-dark font-semibold">Question {qIdx + 1}</Label>
                          <Button
                            type="button"
                            data-testid={`remove-question-${qIdx}`}
                            onClick={() => removeQuestion(qIdx)}
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>

                        <Textarea
                          data-testid={`question-text-${qIdx}`}
                          value={q.question}
                          onChange={(e) => updateQuestion(qIdx, 'question', e.target.value)}
                          placeholder="Enter your question..."
                          className="mb-4"
                          rows={2}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          {q.options.map((opt, oIdx) => (
                            <div key={oIdx} className="flex items-center gap-2">
                              <input
                                type="radio"
                                name={`correct-${qIdx}`}
                                checked={q.correct_answer === oIdx}
                                onChange={() => updateQuestion(qIdx, 'correct_answer', oIdx)}
                                className="w-4 h-4 text-brand-blue"
                              />
                              <span className="font-medium text-sm w-6">{String.fromCharCode(65 + oIdx)}.</span>
                              <Input
                                data-testid={`option-${qIdx}-${oIdx}`}
                                value={opt}
                                onChange={(e) => updateOption(qIdx, oIdx, e.target.value)}
                                placeholder={`Option ${String.fromCharCode(65 + oIdx)}`}
                                className="flex-1"
                              />
                            </div>
                          ))}
                        </div>

                        <div>
                          <Label className="text-sm text-slate-600">Explanation (optional)</Label>
                          <Input
                            data-testid={`explanation-${qIdx}`}
                            value={q.explanation}
                            onChange={(e) => updateQuestion(qIdx, 'explanation', e.target.value)}
                            placeholder="Explain why this answer is correct..."
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </form>

            <div className="p-6 border-t border-slate-200 flex justify-end gap-3">
              <Button
                type="button"
                data-testid="cancel-quiz-btn"
                onClick={() => setShowModal(false)}
                variant="outline"
              >
                Cancel
              </Button>
              <Button
                data-testid="save-quiz-btn"
                onClick={handleSubmit}
                className="btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {editingQuiz ? 'Update Quiz' : 'Create Quiz'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
